import useSWR from 'swr';
import { useSearchParams } from 'react-router-dom';
import { getCollection, getCollections } from '@/api/collections';
import { useT } from '@/contexts/I18nContext';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Select } from '@/components/ui/select';
import { Card } from '@/components/ui/card';

export default function CollectionFilter({ domain }: { domain: 'table' | 'shelf' }) {
  const { t } = useT();
  const [params, setParams] = useSearchParams();
  const selected = params.get('collection') || '';
  const { data, error } = useSWR(['collections', domain], () => getCollections(domain).then(r => r.data));
  const { data: current, error: detailError } = useSWR(selected && selected !== 'none' ? ['collection', selected] : null,
    () => getCollection(selected).then(r => r.data), { refreshInterval: 15000 });
  return <Card className="p-4 space-y-2">
    <label className="flex items-center gap-3 text-sm">{t('collection.label')}
      <Select className="max-w-full" value={selected}
        onChange={e => setParams(prev => {
          const next = new URLSearchParams(prev);
          if (e.target.value) next.set('collection', e.target.value); else next.delete('collection');
          next.set('view', e.target.value && e.target.value !== 'none' ? 'ai' : 'unlabelled');
          next.delete('ids');
          return next;
        })}>
        <option value="">{t('collection.all')}</option>
        <option value="none">{t('collection.none')}</option>
        {current && !data?.collections.some(c => c.id === current.id) &&
          <option value={current.id}>{current.name}</option>}
        {data?.collections.map(c => <option key={c.id} value={c.id}>{c.name} ({c.total})</option>)}
      </Select>
    </label>
    {(error || detailError) && <p role="alert" className="text-destructive">{t('collection.loadError')}</p>}
    {current && <>
      <p className="whitespace-pre-wrap text-sm">{current.description}</p>
      {/^https:\/\//.test(current.source_ref) && <a className="text-sm underline" href={current.source_ref}
        target="_blank" rel="noreferrer">{t('collection.source')}</a>}
      <p className="text-xs text-muted-foreground">{t('collection.counts')}: {current.total} ·
        {t('collection.ai')}: {current.ai} · {t('collection.human')}: {current.human} ·
        {t('collection.remaining')}: {current.unlabelled}</p>
      {current.status !== 'ready' && <p className="text-sm">{t('collection.status.' + current.status)}</p>}
    </>}
  </Card>;
}

export function CollectionRemoveDialog({ open, onClose, onChoose }: {
  open: boolean; onClose: () => void; onChoose: (action: 'unlink' | 'delete') => void;
}) {
  const { t } = useT();
  return <Dialog open={open} onOpenChange={value => { if (!value) onClose(); }}>
    <DialogContent>
      <DialogTitle>{t('collection.removeTitle')}</DialogTitle>
      <p className="my-3 text-sm">{t('collection.removeHelp')}</p>
      <div className="flex flex-wrap gap-2">
        <Button onClick={() => onChoose('unlink')}>{t('collection.unlink')}</Button>
        <Button variant="destructive" onClick={() => onChoose('delete')}>{t('collection.delete')}</Button>
        <Button variant="outline" onClick={onClose}>{t('common.cancel')}</Button>
      </div>
    </DialogContent>
  </Dialog>;
}
